import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchLegalRequest, setLegalStatus, type AdminLegalRequest } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'

/**
 * A19.02 — Huquqiy so'rov kartasi + holat. GET /admin/legal/:id, POST
 * /admin/legal/:id/status (backend `697c6be`). Bajarilганда/rad etilганда yoziladi.
 */
export function LegalCard() {
  const t = useT()
  const { can } = useAuth()
  const { toast } = useToast()
  const { id = '' } = useParams()
  const [req, setReq] = useState<AdminLegalRequest | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchLegalRequest(Number(id), ac.signal)
      .then((r) => {
        if (alive) setReq(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const setStatus = async (status: string) => {
    setBusy(true)
    try {
      await setLegalStatus(Number(id), status, note)
      toast(t.legal.statusChanged, { tone: 'success' })
      setReload((n) => n + 1)
    } catch (e) {
      toast(t.legal.createFailed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setBusy(false)
    }
  }

  const kindLabel = (k: string) => (t.legal as Record<string, string>)[k] ?? k

  return (
    <>
      <PageHeader
        title={req ? `#${req.id} · ${kindLabel(req.kind)}` : t.legal.card}
        screenId="A19.02"
        actions={
          <Link
            to="/legal"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.legal.queue}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !req ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-xl space-y-3">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-3">
                <StatusBadge status={req.status === 'pending' ? 'pending' : req.status === 'fulfilled' ? 'active' : 'deleted'} />
              </div>
              <Field label={t.legal.kind}>{kindLabel(req.kind)}</Field>
              <Field label={t.legal.authority}>{req.authority}</Field>
              <Field label={t.legal.reference}>{req.reference || '—'}</Field>
              <Field label={t.legal.jurisdiction}>{req.jurisdiction || '—'}</Field>
              <Field label={t.legal.affected}>
                {req.affected_user_id ? (
                  <Link to={`/users/${req.affected_user_id}`} className="text-accent hover:underline">
                    {req.username ? `@${req.username}` : `#${req.affected_user_id}`}
                  </Link>
                ) : (
                  '—'
                )}
              </Field>
              <Field label={t.legal.details}>
                <span className="whitespace-pre-wrap">{req.details || '—'}</span>
              </Field>
              <Field label={t.col.time}>
                <RelativeTime value={req.created_at} />
              </Field>
              {req.status !== 'pending' && <Field label={t.appeals.decisionNote}>{req.resolution || '—'}</Field>}
            </div>

            {can(PERMISSIONS.USERS_BAN) && (
              <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                <div className="mb-2 text-sm font-medium text-secondary">{t.legal.updateStatus}</div>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t.legal.resolutionPlaceholder}
                  rows={2}
                  className="mb-3 w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
                />
                <div className="flex flex-wrap gap-2">
                  {(['pending', 'fulfilled', 'rejected'] as const).map((st) => (
                    <button
                      key={st}
                      disabled={busy || req.status === st}
                      onClick={() => setStatus(st)}
                      className={cn(
                        'rounded-md border px-3 py-1.5 text-sm disabled:opacity-50',
                        st === 'fulfilled'
                          ? 'border-success text-success hover:bg-success hover:text-white'
                          : st === 'rejected'
                            ? 'border-danger text-danger hover:bg-danger hover:text-white'
                            : 'border-border-default text-secondary hover:text-primary',
                      )}
                    >
                      {st === 'pending' ? t.mod.pending : st === 'fulfilled' ? t.legal.fulfilled : t.legal.rejected}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
